import React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router';
import {
  DEFAULT_REDIRECT_AFTER_LOGIN,
  FORGOT_PASSWORD_INDEX,
  LOGIN_INDEX,
  RESET_PASSWORD_INDEX,
  SSO_INDEX,
  SUPPORT_LOGIN_INDEX,
} from '../constants/routes';
import LoginWrapper from './login-wrapper';
import SupportLoginForm from './support-login-form';
import { NAV_EXTERNALS } from '../utils/nav';
import ForgotPassword from './forgot-password-form';
import ResetPassword from './reset-password-form';
import Sso from './sso-form';
import LoginFormWizard from './login-form/wizard';
import { useInitializeApplication, useMainData, useToggleWorkingScreen } from './providers/main/hooks';
import { STEP_PASSWORD, STEP_USERNAME } from './providers/auth/context';
import { useAuthData } from './providers/auth/hooks';
import { Box } from '@mui/material';
import { LoadingCentered } from '@c42/component-library';

const Main: React.FC = () => {
  const location = useLocation();

  const { isInitialized, isValidatingToken } = useMainData();
  const initializeApplication = useInitializeApplication();
  const toggleWorkingScreen = useToggleWorkingScreen();

  const { currentStep, serverType } = useAuthData();
  const isStorageNode = serverType?.storage;

  React.useEffect(() => {
    toggleWorkingScreen(true);

    const { pathname, search } = location;
    const query = Object.fromEntries(new URLSearchParams(search));
    initializeApplication({ pathname, query });
  }, []);

  const isForgotPasswordVisible = (): boolean => {
    // Show link on only the first two steps of login form.
    const allowedSteps = [STEP_USERNAME, STEP_PASSWORD];
    const isStepAllowed = allowedSteps.indexOf(currentStep) > -1;

    // Show link on all routes except the forgotten password route.
    const isRouteAllowed = location.pathname !== FORGOT_PASSWORD_INDEX;

    // Do not show "forgot password" on storage node login.
    const showAccountRecoveryLinks = !isStorageNode;

    return showAccountRecoveryLinks && isStepAllowed && isRouteAllowed;
  };

  if (isValidatingToken) {
    return (
      <LoginWrapper isForgotPasswordVisible={false}>
        <Box height={100} position="relative">
          <LoadingCentered />
        </Box>
      </LoginWrapper>
    );
  }

  if (isInitialized) {
    const RedirectToConsole = () => {
      NAV_EXTERNALS.cypressLocationReplace(DEFAULT_REDIRECT_AFTER_LOGIN);
      return null;
    };

    return (
      <LoginWrapper isForgotPasswordVisible={isForgotPasswordVisible()}>
        <Switch>
          <Route path={FORGOT_PASSWORD_INDEX} component={ForgotPassword} />
          <Route path={LOGIN_INDEX} component={LoginFormWizard} />
          <Route path={RESET_PASSWORD_INDEX} component={ResetPassword} />
          <Route path={SSO_INDEX} component={Sso} />
          <Route path={SUPPORT_LOGIN_INDEX} component={SupportLoginForm} />
          <Route path="/landing" component={RedirectToConsole} />
          <Route>
            <Redirect to={LOGIN_INDEX} />
          </Route>
        </Switch>
      </LoginWrapper>
    );
  }

  return <div />;
};

export default Main;
